<template>
  <div class="description-container">
    <div :class="['text-container', { 'no-gradient': showFullText || !isTextTooLong }]">
      <span class="no-transform" v-html="displayedText"></span>
    </div>
    <Button variant="ghost" size="small" v-if="isTextTooLong" @click="toggleText" class="show-more-button">
      {{ showFullText ? 'See Less ↑' : 'See More ↓' }}
    </Button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

import { Button } from '@tallo/design-system';

import { PropertyDto } from '../../model/property';

const props = defineProps<{
  property: PropertyDto | null | undefined;
}>();

const maxTextLength = 250;
const textLengthWithoutEllipsis = maxTextLength - 6;
const showFullText = ref(true);
const description = computed(() => props.property?.description || '');
const isTextTooLong = computed(() => description.value.length > maxTextLength);
const displayedText = computed(() => {
  if (showFullText.value || !isTextTooLong.value) {
    return description.value;
  } else {
    // Trim the description to a length that accommodates the ellipsis
    let trimmedText = description.value.substring(0, textLengthWithoutEllipsis);

    // Find the last space to avoid cutting in the middle of a word
    let lastSpaceIndex = trimmedText.lastIndexOf(' ');

    // If there's no space (a very long word), trim the text directly
    if (lastSpaceIndex === -1) {
      lastSpaceIndex = textLengthWithoutEllipsis;
    }

    // Replace the end of the text with an ellipsis
    return trimmedText.substring(0, lastSpaceIndex) + '...';
  }
});

const toggleText = () => {
  showFullText.value = !showFullText.value;
};
</script>

<style scoped lang="scss">
.text-container {
  position: relative;
  max-height: 31.25rem;
  overflow: hidden;

  .no-transform {
    white-space: break-spaces;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3.125rem;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0), #fff 100%);
  }

  &.no-gradient::after {
    background: none;
  }
}

button {
  margin-top: 0.645rem;
}
</style>
